import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { clearModuleRefusal } from '@/libs/license'
import type { IProperty } from '@/types/Property'
import type { IRoom } from '@/types/Room'
import type { IUser } from '@/types/User'

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string

    // What this account may do, as answered by GET /me. Every screen branches on this
    // and never on a role name: a button that always comes back 403 is a lie, so it is
    // not drawn (docs/PRAVICE.md §1).
    permissions: string[]
    /** The centres this account works in. Empty means every centre. */
    facility_ids?: string[] | null
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    // The property whose rooms this phone is cleaning. Same key and same meaning as in
    // the reception portal — a housekeeper works one house per shift, so it is chosen
    // once and remembered rather than asked for on every screen.
    const propertyId = ref(localStorage.getItem('propertyId') || '')

    const properties = ref<IProperty[]>([])

    // The rooms of that property, kept by id: a task carries a room_id and nothing
    // else, so without this map the sheet is a list of ObjectIDs. Loaded once per
    // session rather than per screen — the inventory does not change during a shift.
    const rooms = ref<Record<string, IRoom>>({})

    // Set when the API refuses the property or room list to this account (403 —
    // both endpoints are guarded by property:read, which a housekeeper does not
    // hold). The screens read it to say what is missing instead of rendering an
    // empty list that looks like a quiet morning.
    const propertiesRestricted = ref(false)
    const roomsRestricted      = ref(false)

    // The accounts a task can be handed to, kept by id. Loaded only when the account
    // holds user:read — GET /users is guarded by it and a supervisor need not hold it.
    // When it is empty because the API refused, usersRestricted says so and the sheet
    // hides assignment altogether rather than offering a control that always 403s.
    const users           = ref<Record<string, IUser>>({})
    const usersRestricted = ref(false)

    // Whether GET /me and the property resolution have been done for this token. The
    // router waits on it, so no screen renders before it knows what it may offer.
    const sessionLoaded = ref(false)

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Values: 'auto' | 'light' | 'dark'.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /** What this account may do. Unknown until GET /me has answered, so it is false. */
    const can = (permission: string) => !!userData.value?.permissions?.includes(permission)

    const getRoom       = (roomId: string) => rooms.value[roomId]
    const getRoomNumber = (roomId: string) => rooms.value[roomId]?.number ?? ''
    const getUserName   = (userId: string) => users.value[userId]?.name ?? ''

    /**
     * Signing out, and the cookie the server holds.
     *
     * The session cookie is written by the server and is HttpOnly, so this page cannot
     * remove it — which is the whole point of it being HttpOnly, and the reason signing
     * out has to ask the server. It is asked and not waited on: whatever the network
     * says, this browser is signed out the moment somebody presses the button.
     */
    const clearServerSession = () => {
        void fetch('/api/pms/v1/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    }

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt

        // A refusal was recorded for whoever was signed in before. This is somebody
        // else — or the same person after the hotel bought the module — so the portal
        // asks again rather than showing them the previous account's locked door.
        clearModuleRefusal()
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        properties.value = []
        rooms.value = {}
        users.value = {}
        propertiesRestricted.value = false
        roomsRestricted.value = false
        usersRestricted.value = false
        sessionLoaded.value = false

        // Signing out ends the only identity the refusal was about. Leaving it set
        // would greet the next person at this screen with a locked door that was never
        // theirs, and the login screen underneath would never be reached.
        clearModuleRefusal()

        clearServerSession()
    }

    const setPropertyId = (id: string) => {
        propertyId.value = id
        localStorage.setItem('propertyId', id)
    }

    const setUserData   = (data: LoggedInUserData | undefined) => userData.value = data
    const setProperties = (list: IProperty[]) => properties.value = list

    const setRooms = (list: IRoom[]) => {
        rooms.value = Object.fromEntries(list.map(room => [room.id, room]))
    }

    const setUsers = (list: IUser[]) => {
        users.value = Object.fromEntries(list.map(user => [user.id, user]))
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    return {
        JWT, locale, userData, loaderActive, colorMode, propertyId, properties, rooms, users,
        propertiesRestricted, roomsRestricted, usersRestricted, sessionLoaded,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, getRoom, getRoomNumber, getUserName,
        setLogin, setLogout, setPropertyId, setUserData, setProperties, setRooms, setUsers,
        startLoading, stopLoading, changeLocale,
    }
})
